import type { Game } from '../game/Game';
import type { GameEvent } from '../game/types';
import { AudioEngine } from './engine';
import { Music } from './music';
import { Sfx } from './sfx';
import { JUKEBOX_ORDER, songByTitle, type Song } from './songs';
import { Voice } from './voice';

export interface AudioSettings {
  sfx: boolean;
  music: boolean;
  voice: boolean;
}

/** Facade that turns game events into sound. */
export class GameAudio {
  readonly engine = new AudioEngine();
  readonly sfx = new Sfx(this.engine);
  readonly music = new Music(this.engine);
  readonly voice = new Voice();
  private jukeboxIndex = 0;
  /** The song to return to after the storm / the Host. */
  private floorSong: string = JUKEBOX_ORDER[0];
  private unsub: (() => void) | null = null;

  constructor() {
    this.voice.onSpeak = (s) => this.engine.duck(0.45, s);
  }

  unlock(): void {
    this.engine.unlock();
    this.voice.unlock();
  }

  apply(settings: AudioSettings): void {
    this.engine.setSfx(settings.sfx);
    this.engine.setMusic(settings.music);
    this.voice.enabled = settings.voice;
    if (!settings.voice) this.voice.cancel();
  }

  set onSongChange(fn: ((song: Song) => void) | null) {
    this.music.onSongChange = fn;
  }

  titleMusic(): void {
    this.music.play('raisins');
  }

  attach(game: Game): void {
    this.detach();
    this.jukeboxIndex = 0;
    this.floorSong = JUKEBOX_ORDER[0];
    this.music.play(this.floorSong);
    this.unsub = game.events.onAny((e) => this.handle(e));
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;
  }

  /** The player feeds the jukebox. */
  nextSong(): Song | null {
    this.jukeboxIndex = (this.jukeboxIndex + 1) % JUKEBOX_ORDER.length;
    this.floorSong = JUKEBOX_ORDER[this.jukeboxIndex] ?? 'grooved';
    this.music.play(this.floorSong);
    return this.music.song;
  }

  private handle(e: GameEvent): void {
    switch (e.type) {
      case 'sfx':
        this.sfx.play(e.name);
        break;
      case 'ticketCreated':
        this.voice.say(e.ticket.tags.includes('usual') ? 'The usual for Hank!' : e.speak);
        break;
      case 'banner':
        if (e.style === 'fight' && e.sub === 'FIGHT!') {
          this.voice.say(`${e.text}. Fight!`, {
            rate: 0.85,
            pitch: 0.55,
            interrupt: true,
            volume: 1,
          });
        } else if (e.style === 'ko' && e.text === 'K.O.!') {
          this.voice.say('K. O.', { rate: 0.7, pitch: 0.5, interrupt: true, volume: 1 });
        }
        break;
      case 'jukebox': {
        const song = songByTitle(e.song);
        if (!song) break;
        if (song.id !== 'hurricane' && song.id !== 'host') {
          this.floorSong = song.id;
          const idx = JUKEBOX_ORDER.indexOf(song.id as (typeof JUKEBOX_ORDER)[number]);
          if (idx >= 0) this.jukeboxIndex = idx;
        }
        this.music.play(song.id);
        break;
      }
      case 'hour':
        if (e.hourIndex === 5) {
          this.floorSong = 'blues';
          this.music.play('blues');
        }
        break;
      case 'storm':
        this.music.play(e.active ? 'hurricane' : this.floorSong);
        break;
      case 'host':
        this.music.play(e.phase === 'start' ? 'host' : this.floorSong);
        if (e.phase === 'start')
          this.voice.say('All. The. Way.', { rate: 0.6, pitch: 0.2, interrupt: true });
        break;
      case 'over':
        this.music.play(
          e.result.reason === 'closed' ? 'blues' : e.result.hostPleased ? 'host' : 'anthem',
        );
        break;
      default:
        break;
    }
  }

  frame(game: Game | null, playing: boolean): void {
    const grillItems = game ? game.grill.filter((s) => s.ticketId !== null).length : 0;
    this.sfx.ambience(grillItems, game?.storm != null, playing);
  }

  pause(): void {
    this.voice.cancel();
    this.engine.suspend();
  }

  resume(): void {
    this.engine.resume();
  }
}
